import * as loadjs from 'loadjs'
import EventManager from '../eventManager/EventManager'
import mediavineLogo from '../../assets/mediavine.svg'
import closeButton from '../../assets/close-x.svg'
import report from '../../assets/report.svg'
import type { Slot } from '../slots/Slot'
import SlotRepository from '../slots/SlotRepository'
const URL = 'https://adreporter.mediavine.com/assets/modal.js'

export class AdReporter {
  loaded = false
  constructor() {
    EventManager.on(EventManager.events.slotWrapperRenderEnded, (s) =>
      this.addReportButton(s)
    )
    document.addEventListener('click', (e) => this.handleReportButtonClick(e))
  }

  handleReportButtonClick(e: MouseEvent) {
    //const clickTarget = e.target as Element
    const clickTarget = e.composedPath()[0] as Element //.classList.contains('mv-ad-report-button')
    if (!clickTarget.classList.contains('mv-ad-report-button')) {
      return
    }

    if (!this.loaded) {
      loadjs(URL, () => {
        this.loaded = true
        this.openAdReporter(clickTarget)
      })
    } else {
      this.openAdReporter(clickTarget)
    }
  }

  private openAdReporter(target: Element) {
    const targetId = target.getAttribute('data-target') || ''
    const adType = target.getAttribute('data-adtype') || 'video'
    const slot =
      adType === 'video'
        ? SlotRepository.getSlotById('video')
        : SlotRepository.getSlotById(targetId)

    window.$mediavine.adReporter.open(targetId, adType, slot)
  }

  addReportButton(slot: Slot) {
    if (slot.type === 'UniversalPlayer' || !slot.model.enable_mvp_branding) {
      return
    }

    const adReporter = document.createElement('mv-ad-reporter')
    adReporter.setAttribute('data-slot-id', slot.id)

    slot.adReporterAnchor.insertAdjacentElement('beforeend', adReporter)
  }
}

class MvAdReporter extends HTMLElement {
  // Keeps track of all MvAdReporters on a page.
  static allAdReporters: MvAdReporter[] = []

  // This click eventListener is meant to close the AdReporter menu if the user clicks anywhere
  // that is NOT part of the AdReporterModal or the Menu itself.
  static bodyClickListener = document.addEventListener('click', (e) => {
    const clickPath = (e.composedPath && e.composedPath()) || []
    const isReporterClick = clickPath.some((el: Element) => {
      return (
        el.tagName === 'MV-AD-REPORTER' || el.id === 'mvAdReporterModalWrapper'
      )
    })

    if (isReporterClick) {
      return
    }

    // Because the user clicked outside of the menu or reporter, close all mv-ad-reporter menus
    MvAdReporter.allAdReporters.forEach((adReporter) => adReporter.closeMenu())
  })

  reporterShadow: ShadowRoot

  isMenuHidden: boolean = true
  menuOverlay: HTMLElement
  logoButton: HTMLImageElement

  constructor() {
    // Always call super first in constructor
    super()

    // write element functionality in here
    this.reporterShadow = this.attachShadow({ mode: 'open' })
    MvAdReporter.allAdReporters.push(this)
  }

  closeMenu() {
    this.menuOverlay.style.display = 'none'
    this.logoButton.src = mediavineLogo
    this.isMenuHidden = true
  }
  openMenu() {
    this.logoButton.src = closeButton
    this.menuOverlay.style.display = 'flex'
    this.isMenuHidden = false

    // This makes sure the menu appears correctly (not off the screen) when it is on
    // the far left hand side of the screen (e.g.: mobile adhesions)
    if (this.menuOverlay.getBoundingClientRect().left < 0) {
      this.menuOverlay.style.left = '15px'
      this.menuOverlay.style.right = 'unset'
    }
  }

  connectedCallback() {
    this.reporterShadow.innerHTML = ` 
      <img id="mediavineSvg" data-pin-nopin=true src=${mediavineLogo} alt='' aria-label='Ad Info' />
      <div class='ad-reporter-menu-backdrop'>
          <div class='ad-reporter-menu'>
          <div class='ad-reporter-menu-item ad-reporter-menu-item-learn-more'>
            <a href='https://www.mediavine.com?utm_source=mediavine&utm_medium=ad&utm_campaign=logo' target='_blank' rel="noopener" aria-label='mediavine'>
              What is this?
            </a>
          </div>
            <div class='ad-reporter-menu-item mv-ad-report-button' data-target='${this.getAttribute(
              'data-slot-id'
            )}' data-adtype=${
      this.getAttribute('data-adtype') || 'display'
    } role='button' aria-label='Report ad'>
              <span>
                <img id="iconReportSvg" data-pin-nopin=true src=${report} alt='' aria-label='Report Ad' /> Report Ad
              </span>
            </div>
          </div>
        </div>
    `
    this.logoButton = this.reporterShadow.querySelector(
      '#mediavineSvg'
    ) as HTMLImageElement
    this.menuOverlay = this.reporterShadow.querySelector(
      '.ad-reporter-menu-backdrop'
    ) as HTMLElement

    this.logoButton?.addEventListener('click', () => {
      if (this.isMenuHidden) {
        this.openMenu()
      } else {
        this.closeMenu()
      }

      return false
    })

    // Apply Styles
    const styleEl = document.createElement('style')
    styleEl.textContent = reporterStyles
    this.reporterShadow.appendChild(styleEl)
  }
}

customElements.define('mv-ad-reporter', MvAdReporter)

const reporterStyles = `
a {
  text-decoration: none;
  color: inherit;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
a:hover {
  text-decoration: underline;
}

#mediavineSvg {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.ad-reporter-menu-backdrop {
  display: none;
  position: absolute;
  bottom: 20px;
  right: 15px;
  width: 120px;
  height: 100px;

  flex-direction: row;
  justify-content: center;
  align-items: center;

  background: transparent;
  box-shadow: rgb(0 0 0 / 15%) 0px 2px 10px;
  border-radius: 10px;
  z-index: 1002;
}

.ad-reporter-menu {
  position: absolute;
  z-index: 1003;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 5px;
  align-items: center;
  justify-content: space-around;
  font-weight: 200;
  font-size: 14px;
  font-family: sans-serif;
}

.ad-reporter-menu .ad-reporter-menu-item {
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;       
}

.ad-reporter-menu #iconReportSvg {
  margin-right: 3px;
}

.ad-reporter-menu .ad-reporter-menu-item:not(:last-child) {
  border-bottom: solid lightgrey 1px;
}

.ad-reporter-menu .ad-reporter-menu-item.mv-ad-report-button > * {
  pointer-events: none;
}
`
